import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { useAppSelector } from "app/hooks";
import { ReactComponent as ChevronLeftIcon } from "components/Icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "components/Icons/chevron-right.svg";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { DirectCampaignRule } from "types/campaigns";
import { defaultRule } from "../utils";
import { MenuItem } from "./MenuItem";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    header: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(5),
    },
    menu: {
      display: "flex",
      flexWrap: "nowrap",
      padding: theme.spacing(2),
      position: "relative",
      overflow: "hidden",
    },
    menuRuleswrapper: {
      display: "flex",
      flexWrap: "nowrap",
      gap: theme.spacing(2),
      backgroundColor: theme.gray50,
      border: `1px solid ${theme.gray100}`,
      borderRadius: "6px",
      overflowX: "auto",
      "&::-ms-overflow-style": "none",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
      paddingRight: theme.spacing(0.5),
    },
    btn: {
      textWrap: "nowrap",
      minWidth: "90px",
      boxShadow: "none !important",
      flexShrink: 0,
      backgroundColor: theme.gray50,
      color: "#232529",
      padding: theme.spacing(3, 5),
      fontSize: "14px",
      lineHeight: "20px",

      "&.MuiButton-contained": {
        backgroundColor: theme.white00,
      },
    },
    arrowSlider: {
      width: "34px",
      height: "36px",
      position: "absolute",
      transform: "translateY(-50%)",
      zIndex: 2,
      top: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    },
    iconWrapper: {
      width: "24px",
      height: "36px",
      backgroundColor: theme.gray50,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "6px",

      "& svg": {
        color: "#232529",
        width: "18px",
        height: "18px",
      },
    },
    warnIcon: {
      color: theme.red500,
      width: "15px",
      height: "15px",
    },
    error: {
      color: theme.red500,
    },
    contextMenu: {
      marginLeft: theme.spacing(2),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      "&:hover": {
        background: (props?: { isSelected: boolean }) =>
          props?.isSelected ? "#2F2657" : theme.primary50,
        borderRadius: "4px",
        color: (props?: { isSelected: boolean }) =>
          props?.isSelected ? theme.white00 : theme.primary500,
      },
    },
    ruleMenu: {
      width: "277px",
    },
    ruleMenuItem: { display: "flex", justifyContent: "space-between" },
    button: {
      color: theme.gray800,
      fontSize: 14,
      "& >span": {
        marginRight: theme.spacing(2),
      },
      "& svg": {
        fontSize: "16px !important",
      },
    },
  }),
);

interface Props {
  rulesCount: number;
  setActiveRule: (ruleIndex: number) => void;
  activeRule: number;
  push: (rule: any) => void;
  remove: (index: number) => void;
  errors: {
    [key: number]: any;
  };
  isEditMode: boolean;
  rules: DirectCampaignRule[];
}

export type ConfirmationModalTrigger = "add" | "delete" | "duplicate";

export const Menu = ({
  rulesCount,
  setActiveRule,
  activeRule,
  push,
  remove,
  errors,
  isEditMode,
  rules,
}: Props) => {
  const [isFirstElemVisible, setIsFirstElemVisible] = useState(true);
  const [isLastElemVisible, setIsLastElemVisible] = useState(true);
  const refButtons = useRef(null);
  const classes = useStyles();
  const { message } = useMessage();
  const defaultLang = useAppSelector(selectDefaultLanguageData);

  useEffect(() => {
    const firstElem = refButtons && (refButtons as any).current.firstChild;
    const lastElem = refButtons && (refButtons as any).current.lastChild;

    const options = {
      root: refButtons.current,
      rootMargin: "0px",
      threshold: 1,
    };

    const observerFirst = new IntersectionObserver((entries) => {
      const [entry] = entries;

      setIsFirstElemVisible(entry.isIntersecting);
    }, options);

    const observerLast = new IntersectionObserver((entries) => {
      const [entry] = entries;
      setIsLastElemVisible(entry.isIntersecting);
    }, options);

    observerFirst.observe(firstElem);
    observerLast.observe(lastElem);

    return () => {
      observerFirst.unobserve(firstElem);
      observerLast.unobserve(lastElem);
    };
  }, [rulesCount]);

  useEffect(() => {
    const activeRuleElement = document.getElementById(`rule-${activeRule}`);

    if (refButtons && refButtons.current && activeRuleElement) {
      (refButtons.current as Element).scrollTo({
        left: activeRuleElement.offsetLeft - activeRuleElement.offsetWidth / 2,
        top: 0,
        behavior: "smooth",
      });
    }
  }, [activeRule]);

  const isMaxRulesCount = rulesCount >= 6;
  const isMinRulesCount = rulesCount <= 1;

  return (
    <header className={classes.header}>
      <div className={classes.menu}>
        <div ref={refButtons} className={classes.menuRuleswrapper}>
          {Array.from(Array(rulesCount).keys()).map((idx) => {
            const isSelected = activeRule === idx;
            const hasError =
              !isEmpty(errors[idx]?.conditions) ||
              !isEmpty(errors[idx]?.effects);
            const ruleData = rules[idx];

            return (
              <MenuItem
                data-test-id={`menu-item-${idx}`}
                isEditMode={isEditMode}
                hasError={!!hasError}
                idx={idx}
                isSelected={isSelected}
                key={idx}
                setActiveRule={setActiveRule}
                isDisabled={{
                  delete: isMinRulesCount,
                  duplicate: isMaxRulesCount,
                }}
                onDelete={() => {
                  remove(activeRule);
                  setActiveRule(activeRule === 0 ? 0 : activeRule - 1);
                }}
                onDuplicate={() => {
                  push(rules[activeRule]);
                  setActiveRule(rulesCount);
                }}
                name={ruleData?.name}
              />
            );
          })}
        </div>
        {!isFirstElemVisible && (
          <div
            className={classes.arrowSlider}
            style={{ left: 0 }}
            onClick={() => {
              if (refButtons.current) {
                (refButtons.current as Element).scrollBy({
                  top: 0,
                  left: -100,
                  behavior: "smooth",
                });
              }
            }}
            data-test-id="menu-left"
          >
            <div className={classes.iconWrapper}>
              <ChevronLeftIcon />
            </div>
          </div>
        )}
        {!isLastElemVisible && (
          <div
            className={classes.arrowSlider}
            style={{ right: 0 }}
            onClick={() => {
              if (refButtons.current) {
                (refButtons.current as Element).scrollBy({
                  top: 0,
                  left: 100,
                  behavior: "smooth",
                });
              }
            }}
            data-test-id="menu-right"
          >
            <div className={classes.iconWrapper}>
              <ChevronRightIcon />
            </div>
          </div>
        )}
      </div>
      <OLButton
        onClick={() => {
          push({
            ...defaultRule,
            translations: [
              defaultLang && {
                description: "",
                locale: defaultLang.code,
                name: "",
              },
            ],
          });
          setActiveRule(rulesCount);
        }}
        startIcon={<PlusIcon />}
        disabled={isMaxRulesCount}
        data-test-id="campaign-rules-add"
        size="small"
        className={classes.button}
      >
        {message({ id: "addRule" })}
      </OLButton>
    </header>
  );
};
